import { useState } from "react";
import { motion } from "framer-motion";
import {
  Package,
  ClipboardCheck,
  Coins,
  MapPin,
  HandCoins,
  Truck,
} from "lucide-react";

export default function HowItWorks() {
  const [role, setRole] = useState(null);

  // ----------------- DATA ------------------
  const yukVeren = [
    {
      icon: <Package size={26} className="text-black" />,
      title: "Yükünü Yayınla",
      desc: "Nereden nereye, malzeme türü ve ağırlığı gir. Yük ilanını anında oluştur.",
    },
    {
      icon: <ClipboardCheck size={26} className="text-black" />,
      title: "Teklifleri İncele",
      desc: "Nakliyecilerden gelen fiyat tekliflerini karşılaştır, en uygun olanı seç.",
    },
    {
      icon: <Coins size={26} className="text-black" />,
      title: "Anlaş ve İletişime Geç",
      desc: "Teklifi kabul et ve nakliyeciyle doğrudan iletişime geç.",
    },
  ];

  const nakliyeci = [
    {
      icon: <MapPin size={26} className="text-black" />,
      title: "Uygun Yükleri Keşfet",
      desc: "Güzergâhına göre sana uygun yükleri listele ve incele.",
    },
    {
      icon: <HandCoins size={26} className="text-black" />,
      title: "Teklif Gönder",
      desc: "Yük detaylarını incele ve fiyat teklifini yükverene ilet.",
    },
    {
      icon: <Truck size={26} className="text-black" />,
      title: "İşi Al ve Yola Çık",
      desc: "Teklifin kabul edilince yükverene ulaş, taşımayı tamamla ve kazancını al.",
    },
  ];

  // ----------------- ANIMATION ------------------
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
    }),
  };

  return (
    <section
      id="how"
      className="py-24 px-6 md:px-12 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto">
        {/* Title */}
        <motion.h2
          className="text-3xl md:text-4xl font-extrabold text-center text-black mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          Nasıl Çalışır?
        </motion.h2>

        {/* -----------------------------------------------------
  MOBIL TASARIM (md:hidden)
------------------------------------------------------ */}
<div className="md:hidden">
  {/* Rol seçimi ekranı */}
  {!role && (
    <div className="space-y-4 mb-10">
      <button
        onClick={() => setRole("yukveren")}
        className="w-full bg-gray-50 border border-gray-300 rounded-2xl p-5 shadow-sm flex items-center gap-4 active:scale-[0.98] transition"
      >
        <div className="bg-gray-200 p-3 rounded-xl">
          <Package className="text-black" size={26} />
        </div>

        <div className="flex flex-col text-left">
          <h3 className="font-semibold text-lg text-black">Yük Verenim</h3>
          <p className="text-sm text-gray-600">
            İlan oluştur, teklifler gelsin.
          </p>
        </div>
      </button>

      <button
        onClick={() => setRole("nakliyeci")}
        className="w-full bg-gray-50 border border-gray-300 rounded-2xl p-5 shadow-sm flex items-center gap-4 active:scale-[0.98] transition"
      >
        <div className="bg-gray-200 p-3 rounded-xl">
          <Truck className="text-black" size={26} />
        </div>

        <div className="flex flex-col text-left">
          <h3 className="font-semibold text-lg text-black">Nakliyeciyim</h3>
          <p className="text-sm text-gray-600">
            Boş dönüş yapma, uygun yükleri bul.
          </p>
        </div>
      </button>
    </div>
  )}

  {/* Rol banner */}
  {role && (
    <div className="text-center mb-8">
      <h3 className="text-xl font-bold">
        {role === "yukveren"
          ? "Yük Veren İçin Adımlar"
          : "Nakliyeci İçin Adımlar"}
      </h3>
    </div>
  )}

  {/* Adımlar */}
  {role && (
    <div className="space-y-6">
      {(role === "yukveren" ? yukVeren : nakliyeci).map((step, i) => (
        <motion.div
          key={i}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={i}
          className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="bg-gray-100 border border-gray-200 p-3 rounded-full">
              {step.icon}
            </div>
            <h4 className="text-base font-semibold text-black">
              {step.title}
            </h4>
          </div>
          <p className="text-gray-600 text-sm mt-1">{step.desc}</p>
        </motion.div>
      ))}
    </div>
  )}

  {/* Geri butonu */}
  {role && (
    <button
      onClick={() => setRole(null)}
      className="mt-8 w-full text-center text-sm text-gray-600 underline"
    >
      ← Rol Seçimine Dön
    </button>
  )}
</div>

        {/* -----------------------------------------------------
          DESKTOP TASARIM (Timeline)
        ------------------------------------------------------ */}
        <div className="hidden md:grid md:grid-cols-2 gap-y-16 gap-x-10 relative">
          {/* Orta çizgi */}
          <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gray-300 transform -translate-x-1/2"></div>

          {/* Yükveren */}
          <div className="space-y-16">
            {yukVeren.map((step, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="relative flex flex-col md:flex-row md:justify-end md:text-right"
              >
                <div className="md:w-1/2 flex flex-col items-center md:items-end">
                  <div className="bg-white border-2 border-black rounded-xl p-6 shadow-sm max-w-sm hover:shadow-md transition">
                    <div className="flex items-center gap-3 justify-center md:justify-end">
                      <div className="bg-gray-100 border border-gray-300 p-3 rounded-full">
                        {step.icon}
                      </div>
                      <h3 className="text-lg font-semibold text-black">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mt-2">{step.desc}</p>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 top-10 w-4 h-4 bg-black rounded-full transform -translate-x-1/2"></div>
              </motion.div>
            ))}
          </div>

          {/* Nakliyeci */}
          <div className="space-y-16">
            {nakliyeci.map((step, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="relative flex flex-col md:flex-row md:justify-start"
              >
                <div className="md:w-1/2 flex flex-col items-center md:items-start">
                  <div className="bg-white border-2 border-black rounded-xl p-6 shadow-sm max-w-sm hover:shadow-md transition">
                    <div className="flex items-center gap-3 justify-center md:justify-start">
                      <div className="bg-gray-100 border border-gray-300 p-3 rounded-full">
                        {step.icon}
                      </div>
                      <h3 className="text-lg font-semibold text-black">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm mt-2">{step.desc}</p>
                  </div>
                </div>
                <div className="hidden md:block absolute left-1/2 top-10 w-4 h-4 bg-black rounded-full transform -translate-x-1/2"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
